import { useCommonAppTranslation } from '@libs/utils/translations'
import { padStart } from 'lodash-es'
import moment from 'moment'

/**
 * Formats the date to set as datepicker input
 * Actually transforms it from 'YYYY-MM-DD' to 'DD/MM/YYYY'
 * @param  {String} date
 * @return {String}
 */
export const formatDate = (
  date,
  inFormat = 'YYYY-MM-DD',
  outFormat = 'DD/MM/YYYY'
) => (date ? moment(date, inFormat).format(outFormat) : null)

// Formats time
export const resetMinutes = date => moment(date).startOf('minute')

export const displayTwoNumber = num => padStart(String(num), 2, '0')

export const formatHoursDecimal = durationInHours =>
  new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(durationInHours)

export const addDaysToDate = (date, dayCount) => {
  const duppedDate = new Date(date)
  duppedDate.setDate(duppedDate.getDate() + Number(dayCount))

  return duppedDate
}

export const frenchZipCodeRegex = /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/

export const removeAccentsAndUpperCase = string =>
  string
    .replace(new RegExp('[ç]', 'g'), 'c')
    .replace(new RegExp('[ñ]', 'g'), 'n')
    .replace(new RegExp('[ÄÂÀÁäâàá]', 'g'), 'a')
    .replace(new RegExp('[ËÊÈÉéèëê]', 'g'), 'e')
    .replace(new RegExp('[ÏÎÌÍïîìí]', 'g'), 'i')
    .replace(new RegExp('[ÖÔÒÓöôòó]', 'g'), 'o')
    .replace(new RegExp('[ÜÛÙÚüûùúµ]', 'g'), 'u')
    .toUpperCase()

export const formatToWeekISO = date => {
  const startOfWeek = moment(date).startOf('isoWeek')
  const endOfWeek = moment(date).endOf('isoWeek')

  const momentDate = moment(date)

  if (
    startOfWeek.get('year') < momentDate.get('year') &&
    momentDate.get('isoWeek') !== 1
  ) {
    return startOfWeek.format('YYYY[-W]WW')
  }

  if (
    endOfWeek.get('year') > momentDate.get('year') &&
    endOfWeek.get('isoWeek') === 1
  ) {
    return endOfWeek.format('YYYY[-W]WW')
  }

  return momentDate.format('YYYY[-W]WW')
}

export const roundToTwoDecimals = number =>
  number ? Number(number.toFixed(2)) : 0

export const truncateToTwoDecimals = number =>
  number ? Math.trunc(number * 100) / 100 : 0

// stopEventPropagation is mainly used to work around react-rnd preventing inputs from
// being focused.
// See https://github.com/bokuweb/react-rnd/issues/403
export const stopEventPropagation = e => e.stopPropagation()

// To display duration as : '1 jour' || '2 jours' || 'Une demie-journée' || '3 jours et demi"
// starsAt && endsAt must be moment() ready
// startHalfDay && endHalfDay are regular 'morning' || 'afternoon' string
export const useDiffToReadableDaysWithHalf = (
  starsAt,
  endsAt,
  startHalfDay,
  endHalfDay
) => {
  const { t } = useCommonAppTranslation()

  if (!starsAt || !endsAt || !startHalfDay || !endHalfDay) {
    return 0
  }

  const isDiffHalf = startHalfDay !== endHalfDay
  const isSameHalf = startHalfDay === endHalfDay
  const isOneDay = starsAt === endsAt && isDiffHalf
  const isHalfOneDay = starsAt === endsAt && isSameHalf

  // if rest has the same half, we dont add 1 day in total
  const durationInDays =
    (isOneDay && 1) || isSameHalf
      ? moment(endsAt).diff(moment(starsAt), 'days')
      : moment(endsAt).diff(moment(starsAt), 'days') + 1

  const isHalf = durationInDays >= 1 && isSameHalf
  const isHalfOverlap = durationInDays === 1 && isSameHalf

  // Only one half day
  if (isHalfOneDay) {
    return t('duration.halfDay')
  }

  // Only one day
  if ((isOneDay || isHalfOverlap) && isDiffHalf) {
    return t('duration.day', { count: 1 })
  }

  // If we have days and half
  if (isHalf) {
    return t('duration.dayAndHalfDay', { count: durationInDays })
  }

  // Only we full days
  return t('duration.day', { count: durationInDays })
}

export const displaySignOperator = (value: number) => {
  if (value === 0) {
    return ''
  }
  return Math.sign(value) === 1 ? '' : '-'
}
